/**
 * Permanente KfW-/Vorsorge-Eingabe in PEStart (nicht Privatkostenkompass, nicht Wizard).
 */

import React from 'react';
import { useShallow } from 'zustand/react/shallow';
import usePEStartStore from '../../store/usePEStartStore';

const INPUT_CLASS =
    'w-full rounded-lg border border-pe-border-strong dark:border-[#333] bg-pe-elevated dark:bg-[#1a1a1a] px-3 py-2.5 text-base text-pe-text dark:text-white outline-none focus:border-[#E6C299]/50';

const NUMBER_INPUT_CLASS = `${INPUT_CLASS} appearance-none [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`;

const LABEL_CLASS =
    'mb-1.5 block text-xs font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400';

function numberFieldValue(n: number): string | number {
    if (!Number.isFinite(n) || n === 0) return '';
    return n;
}

function parseNonNegative(raw: string): number {
    const cleaned = String(raw ?? '')
        .replace(/\s/g, '')
        .replace(',', '.');
    if (cleaned === '' || cleaned === '-') return 0;
    const n = Number(cleaned);
    if (!Number.isFinite(n) || n < 0) return 0;
    return n;
}

const PEStartKfWProfilPanel: React.FC<{ className?: string }> = ({ className }) => {
    const {
        privateAltersvorsorge,
        liquiditaetspufferMonat,
        fachlicheQualifikation,
        berufserfahrung,
        kaufmaennischeEignung,
        executiveSummaryNotes,
        setPrivateAltersvorsorge,
        setLiquiditaetspufferMonat,
        setFachlicheQualifikation,
        setBerufserfahrung,
        setKaufmaennischeEignung,
        setExecutiveSummaryNotes,
    } = usePEStartStore(
        useShallow((s) => ({
            privateAltersvorsorge: s.privateAltersvorsorge,
            liquiditaetspufferMonat: s.liquiditaetspufferMonat,
            fachlicheQualifikation: s.fachlicheQualifikation,
            berufserfahrung: s.berufserfahrung,
            kaufmaennischeEignung: s.kaufmaennischeEignung,
            executiveSummaryNotes: s.executiveSummaryNotes,
            setPrivateAltersvorsorge: s.setPrivateAltersvorsorge,
            setLiquiditaetspufferMonat: s.setLiquiditaetspufferMonat,
            setFachlicheQualifikation: s.setFachlicheQualifikation,
            setBerufserfahrung: s.setBerufserfahrung,
            setKaufmaennischeEignung: s.setKaufmaennischeEignung,
            setExecutiveSummaryNotes: s.setExecutiveSummaryNotes,
        }))
    );

    return (
        <section
            className={`rounded-2xl border border-pe-border dark:border-[#2a2a2a] bg-pe-card dark:bg-[#0f0f0f] p-5 ${className ?? ''}`}
        >
            <header className="mb-5 border-b border-pe-border dark:border-[#222] pb-4">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-500 dark:text-slate-500">
                    PEStart · KfW / Bank
                </p>
                <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-pe-accent-text dark:text-[#E6C299]">
                    Qualifikation, Vorsorge &amp; Vorhaben
                </h2>
                <p className="mt-2 text-sm text-stone-500 dark:text-slate-500">
                    Nur für Businessplan / Förderantrag — nicht Teil des Privatkostenkompass.
                </p>
            </header>

            <div className="mb-6 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                    1 · Persönliche &amp; fachliche Qualifikation (KfW-Profil)
                </p>
                <div>
                    <label className={LABEL_CLASS}>Fachliche Qualifikation &amp; Titel</label>
                    <textarea
                        value={fachlicheQualifikation || ''}
                        onChange={(e) => setFachlicheQualifikation(e.target.value)}
                        rows={3}
                        placeholder="Meistertitel, Fachschulabschlüsse, Zertifikate…"
                        className={`${INPUT_CLASS} resize-y leading-relaxed`}
                    />
                </div>
                <div>
                    <label className={LABEL_CLASS}>Berufserfahrung &amp; Praxis</label>
                    <textarea
                        value={berufserfahrung || ''}
                        onChange={(e) => setBerufserfahrung(e.target.value)}
                        rows={3}
                        placeholder="Jahre im Beruf, Salonleitung, Führungsverantwortung…"
                        className={`${INPUT_CLASS} resize-y leading-relaxed`}
                    />
                </div>
                <div>
                    <label className={LABEL_CLASS}>Kaufmännische &amp; unternehmerische Eignung</label>
                    <textarea
                        value={kaufmaennischeEignung || ''}
                        onChange={(e) => setKaufmaennischeEignung(e.target.value)}
                        rows={3}
                        placeholder="Warum Tragfähigkeit / Führung am Markt…"
                        className={`${INPUT_CLASS} resize-y leading-relaxed`}
                    />
                </div>
            </div>

            <div className="mb-6 space-y-3 border-t border-pe-border dark:border-[#222] pt-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                    2 · Zusätzliche private Absicherung (KfW-Vorsorge)
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                        <label className={LABEL_CLASS}>Private Altersvorsorge (€ / Monat)</label>
                        <input
                            type="number"
                            min={0}
                            step={10}
                            inputMode="decimal"
                            placeholder="0"
                            value={numberFieldValue(privateAltersvorsorge)}
                            onChange={(e) =>
                                setPrivateAltersvorsorge(parseNonNegative(e.target.value))
                            }
                            onFocus={(e) => e.target.select()}
                            className={NUMBER_INPUT_CLASS}
                        />
                    </div>
                    <div>
                        <label className={LABEL_CLASS}>
                            Aufbau privater Liquiditätspuffer (€ / Monat)
                        </label>
                        <input
                            type="number"
                            min={0}
                            step={10}
                            inputMode="decimal"
                            placeholder="0"
                            value={numberFieldValue(liquiditaetspufferMonat)}
                            onChange={(e) =>
                                setLiquiditaetspufferMonat(parseNonNegative(e.target.value))
                            }
                            onFocus={(e) => e.target.select()}
                            className={NUMBER_INPUT_CLASS}
                        />
                    </div>
                </div>
            </div>

            <div className="space-y-3 border-t border-pe-border dark:border-[#222] pt-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                    3 · Vorhabensbeschreibung &amp; Executive Summary
                </p>
                <textarea
                    value={executiveSummaryNotes || ''}
                    onChange={(e) => setExecutiveSummaryNotes(e.target.value)}
                    rows={7}
                    placeholder="Leitfragen: Vorhaben, Markt/Standort, Timing, USP, geplante Kapazität…"
                    className={`${INPUT_CLASS} min-h-[10rem] resize-y leading-relaxed`}
                />
            </div>
        </section>
    );
};

export default PEStartKfWProfilPanel;
